import ProductCardPriceLegacy from '@components/components/ProductComponentsLegacy/ProductCardPrice/ProductCardPrice';
import productTypes from '@components/components/ProductRecommender/ProductRecommender.config.js';
import ErrorRecommender from '@components/components/ProductRecommender/ErrorRecommender/ErrorRecommender';
import { InputFieldNumeric } from '@components/components';
import {
  useLocalStorage,
  useSessionStorage,
  useTracking,
} from '@components/hooks';
import fetchData from '@components/components/ProductRecommender/hooks/fetchData';
import styles from '@components/components/ProductRecommender/ProductRecommender.module.scss';
import { usePageData } from '@components/context';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import isEqual from 'lodash.isequal';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Content,
  Counter,
  Heading,
  FontAwesomeProIcon,
  CardBody,
} from '@wienenergiegithub/ui-next';
import {
  useFormStore,
  useRadioStore,
  Form,
  FormCheckbox,
  FormGroup,
  FormSubmit,
  Radio,
  RadioGroup,
} from '@ariakit/react';
import classNames from 'classnames';
import React, { useCallback, useEffect, useState, useRef } from 'react';

const ProductRecommender = ({
  initType = 'strom',
  variant = 'full',
  header = false,
  defaultValues,
  defaultVariableNumber = 2,
  options,
  product,
  products,
  price,
  dialog,
  onChange,
  onSubmit,
  className,
  trackingElement,
  defaultAktionsCode,
}) => {
  const track = useTracking();
  const initialRender = useRef(true);
  const [addressStorage] = useLocalStorage('address-check');
  const [showLimitBox, setShowLimitBox] = useState(false);
  const [limitBox, setLimitBox] = useState('');
  const { generalData } = usePageData();
  const [serverError, setServerError] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);
  const aktion = urlParams.get('aktion');
  // for product detail page we get the aktion from params
  // for product category we get it from generalData
  // for general product recommender we get it from blockdata (i.e. props)
  const defaultAktion =
    aktion || generalData?.default_code || defaultAktionsCode;
  // TODO should we delete the param after reading it?

  // Check if in product list there's a "No bindung" option
  const isNoBindung =
    (products || []).filter(({ content }) =>
      content?.options?.bindung?.some(
        ({ label }) => label === 'Keine Bindung' || label === 'Ungebunden',
      ),
    ).length > 0;

  const [productType, setProductType] = useState(initType);

  const radioStore = useRadioStore({
    value: productType,
    setValue: setProductType,
  });

  // set productType when initType changes
  useEffect(() => {
    setProductType(initType);
  }, [initType]);

  const activeProduct = productTypes[productType];

  const [sessionState, setSessionState] = useSessionStorage(
    `${productType}-recommender`,
    {
      ...(defaultValues || {
        usage: 2300,
        count: 2,
        priceGuarantee: false,
        ecoMode: false,
        nobindung: false,
        zipcode: null,
      }),
      zipcode: addressStorage?.plzOrt?.split(' ')[0] || null,
      product,
      type: initType,
      count: defaultVariableNumber,
      usage: activeProduct?.calculate(defaultVariableNumber),
    },
  );

  const [isInNetwork, setIsInNetwork] = useState(true);
  const [formData, setFormData] = useState(sessionState);
  const form = useFormStore({ values: formData, setValues: setFormData });
  const formState = form.useState();
  const [observeCondition, setObserveCondition] = useState(false);

  // we track with two different events (using trackingObject)
  // 1) onSubmit (only for general product recommender)
  // 2) user has interacted with the form and scrolls to the product cards
  const trackingObject = {
    event: 'product-recommendation-show',
    ...formData,
    tarifCommitment: formData.nobindung,
    type: productType,
    // business product recommender does not have a person counter
    count: productType.includes('business') ? null : formData.count,
  };

  // track when user scrolls to the product cards and has interacted with the form
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries, intersectionObserver) => {
        // not on initial render
        if (initialRender.current) {
          initialRender.current = false;
          intersectionObserver.unobserve(entries[0].target);
        } else if (entries[0].isIntersecting && observeCondition) {
          // track and unobserve
          track(trackingObject);
          intersectionObserver.unobserve(entries[0].target);
        }
      },
      // when 80% of the productCardSlider is visible
      {
        threshold: 0.8,
      },
    );

    if (trackingElement?.current) {
      observer.observe(trackingElement?.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [products]);

  form.useValidate(async () => {
    const { zipcode, usage } = formData;

    // check if zipcode is within network (versorgungsgebiet)
    if (!isInNetwork) {
      form.setError(
        'zipcode',
        'Für die angegebene Postleitzahl wurde kein Angebot gefunden',
      );
    }

    // check if zipcode is valid
    if (
      variant !== 'general' &&
      (!zipcode || (zipcode.trim().length !== 0 && zipcode.trim().length !== 4))
    ) {
      form.setError('zipcode', 'Bitte geben Sie eine gültige Postleitzahl an');
    }

    if (
      variant === 'general' &&
      !zipcode &&
      !zipcode.trim().length === 4 &&
      addressStorage?.plzOrt?.split(' ')[0]
    ) {
      form.setValue('zipcode', addressStorage.plzOrt.split(' ')[0]);
    }

    if (usage <= 0) {
      form.setError('usage', 'Bitte geben Sie Ihren Jahresverbrauch ein');
    } else if (usage > activeProduct.limit && !activeProduct.showLimitAsBox) {
      form.setError(
        'usage',
        `Bitte geben Sie einen Jahresverbrauch bis maximal ${activeProduct?.limit?.toLocaleString()} kWh ein.`,
      );
    }

    // Remove the limit box conditionally based on the query
    if (productType.includes('business') && usage < 100000) {
      setShowLimitBox(false);
    }

    if (!isEqual(formData, sessionState)) {
      setSessionState({ ...formData, type: productType });
      setObserveCondition(true);
      initialRender.current = false;
    }
  });

  form.useSubmit(async () => {
    // track on submit only if it is general product recommender
    if (variant === 'general') {
      track(trackingObject);
    }

    if (onSubmit) {
      onSubmit({
        products: formData,
        type: productType,
        recommenderState: sessionState,
      });
    }
  });

  // Counter update
  const handleCounterAction = action => {
    switch (action) {
      case 'increment':
        if (activeProduct.max && activeProduct.max === formData.count) {
          break;
        }
        form.setValue('count', formData.count + 1);
        form.setValue('usage', activeProduct.calculate(formData.count + 1));
        break;
      case 'decrement':
        if (activeProduct.min && activeProduct.min === formData.count) {
          break;
        }
        form.setValue('count', formData.count - 1);
        form.setValue('usage', activeProduct.calculate(formData.count - 1));
        break;
      default:
    }
  };

  const icon = activeProduct.images({
    count: formData.count,
  });

  const debouncedFetchData = useCallback(
    debounce(params => {
      fetchData(productType, params)
        .then(data => {
          if (onChange) {
            onChange({
              products: data.data,
              type: productType,
              recommenderState: sessionState,
            });
          }
          setIsInNetwork(true);
        })
        .catch(error => {
          const { message: errorMessage } = error;
          const { usage, ...rest } = params;

          // The errorMessage comes from BE (product group)
          if (errorMessage.includes('limitbox')) {
            setShowLimitBox(true);
            setLimitBox(JSON.parse(errorMessage));

            // if limitBox is shown, we fetch data with default values
            fetchData(productType, rest).then(data => {
              if (onChange) {
                onChange({
                  products: data.data,
                  type: productType,
                  recommenderState: sessionState,
                });
              }
            });
          } else if (errorMessage.includes('500')) {
            setServerError(true);
          } else if (errorMessage.includes('Postleitzahl')) {
            setIsInNetwork(false);
            // if zipcode is outside our network, we fetch data without zipcode
            // so product cards are shown with default values
            fetchData(productType, rest).then(data => {
              if (onChange) {
                onChange({
                  products: data.data,
                  type: productType,
                  recommenderState: sessionState,
                });
              }
            });
          }
        });
    }, 500),
    [productType],
  );

  const handleNewCheck = () => {
    setServerError(false);
  };

  useEffect(() => {
    const params = {
      ...sessionState,
      options,
      zipcode: sessionState?.zipcode || options?.zipcode,
      product,
      defaultAktion,
      type: productType,
    };

    // fetch data either when there is no error
    // or when there is only one error and it is zipcode
    const noErrors = Object.keys(formState.errors).length === 0;
    const imVersorgungsgebiet =
      Object.keys(formState.errors).length === 1 &&
      formState?.errors?.zipcode ===
        'Für die angegebene Postleitzahl wurde kein Angebot gefunden';

    if (noErrors || imVersorgungsgebiet) {
      debouncedFetchData(params);
    }
  }, [productType, options, sessionState]);

  // when switching Type we update kwh
  useEffect(() => {
    form.setValue(
      'usage',
      sessionState?.usage || activeProduct.calculate(sessionState.count),
    );
  }, [productType]);

  // make sure zipcode default value in form is set in this hierarchy:
  // 1. sessionState, 2. addressStorage, 3. empty string.
  useEffect(() => {
    if (
      variant !== 'general' &&
      addressStorage &&
      addressStorage.plzOrt.split(' ').length > 1
    ) {
      form.setValue(
        'zipcode',
        sessionState?.zipcode || addressStorage?.plzOrt.split(' ')[0] || '',
      );
    }
    // in case of general product recommender the order is different
    // addressStorage is the most reliable source
    if (
      variant === 'general' &&
      addressStorage &&
      addressStorage.plzOrt.split(' ').length > 1
    ) {
      form.setValue(
        'zipcode',
        addressStorage?.plzOrt.split(' ')[0] || sessionState?.zipcode || '',
      );
    }
  }, [addressStorage]);

  // This function is for WAI optimizing
  const handleKeyPress = ({ key }, newVal) => {
    if (key === 'Enter') {
      radioStore.setValue(newVal);
    }
  };

  return (
    <div
      className={classNames(styles.row, styles[variant], {
        [styles.noHeader]: !header,
      })}
      id="product-recommender"
    >
      <div
        className={classNames(
          styles.container,
          styles[variant],
          {
            [styles.reducedContainer]: price,
          },
          className,
        )}
      >
        {showLimitBox && !!limitBox?.message && (
          <Alert
            className={styles.alert}
            details={limitBox.message}
            handleClose={() => {
              setShowLimitBox(false);
            }}
            hasCloseButton
            icon="bell"
            title={limitBox?.title}
            type="info"
          />
        )}
        {!serverError && (
          <Card>
            <CardBody className={styles.cardBody}>
              <div className={styles.toggle}>
                {variant === 'full' && (
                  <RadioGroup
                    aria-label="Tarif Empfehler, Energietyp"
                    className={styles.switcher}
                    store={radioStore}
                  >
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label
                      className={classNames(styles.switch, {
                        [styles.switchActive]: productType === 'strom',
                      })}
                      onKeyDown={e => handleKeyPress(e, 'strom')}
                      role="presentation"
                    >
                      <Radio
                        className={styles.switchRadio}
                        store={radioStore}
                        value="strom"
                      />
                      <FontAwesomeProIcon
                        className={styles.switchIcon}
                        icon="bolt"
                        role="presentation"
                        type="solid"
                      />
                      Strom
                    </label>
                    {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                    <label
                      className={classNames(styles.switch, {
                        [styles.switchActive]: productType === 'gas',
                      })}
                      onKeyDown={e => handleKeyPress(e, 'gas')}
                      role="presentation"
                    >
                      <Radio
                        className={styles.switchRadio}
                        store={radioStore}
                        value="gas"
                      />
                      <FontAwesomeProIcon
                        className={styles.switchIcon}
                        icon="fire-alt"
                        role="presentation"
                        type="solid"
                      />
                      Gas
                    </label>
                  </RadioGroup>
                )}
              </div>
              <div className={styles.contentWrapper}>
                <Form
                  id="product-recommendation"
                  resetOnSubmit={false}
                  store={form}
                >
                  <FormGroup
                    aria-describedby={undefined}
                    aria-label="Verbrauch"
                    aria-labelledby={undefined}
                    className={styles.formWrapper}
                    name="basics"
                  >
                    <div className={styles.basicGroup}>
                      {variant === 'full' && (
                        <img alt="" className={styles.icon} src={icon} />
                      )}
                      {price && (
                        <div className={styles.cardPrice}>
                          <Heading className={styles.heading} size="h5">
                            Ihre Vorberechnung
                          </Heading>
                          <ProductCardPriceLegacy
                            additional={price.subline}
                            price={price.main_price}
                            subtextCenter
                            theme="highlight"
                            unit={price.unit}
                            withMore={dialog}
                          />
                        </div>
                      )}
                      {activeProduct.showCounter && (
                        <Counter
                          count={formData.count}
                          i18n={{
                            increment: `${activeProduct.labelContext} erhöhen`,
                            decrement: `${activeProduct.labelContext} verringern`,
                          }}
                          max={activeProduct.max}
                          min={activeProduct.min}
                          onAction={handleCounterAction}
                        >
                          <span
                            aria-live="polite"
                            className={styles.counter}
                            data-purpose="productrecommender-label"
                            role="region"
                          >
                            {activeProduct.getLabel(formData.count)}
                          </span>
                        </Counter>
                      )}
                      <div className={styles.formGroup}>
                        <div className={styles.usageInputWrapper}>
                          <InputFieldNumeric
                            // "text" & "numeric" needed to prevent scroll wheel
                            className={styles.inputField}
                            inputMode="numeric"
                            label="Jahresverbrauch:"
                            name="usage"
                            pattern="\d*"
                            store={form}
                            suffix="kWh"
                            type="text"
                            variant="closed"
                          />
                        </div>
                        {variant !== 'general' && (
                          <InputFieldNumeric
                            // "text" & "numeric" needed to prevent scroll wheel
                            className={styles.inputField}
                            inputMode="numeric"
                            label="Postleitzahl:"
                            name="zipcode"
                            pattern="\d*"
                            placeholder="z.B. 1020"
                            store={form}
                            type="text"
                            variant="closed"
                          />
                        )}
                      </div>
                    </div>
                  </FormGroup>
                  {variant !== 'reduced' && (
                    <div className={styles.filterWrapper}>
                      {variant === 'general' && (
                        <Heading size="h6">Was ist Ihnen wichtig?</Heading>
                      )}
                      <FormGroup
                        aria-describedby={undefined}
                        aria-label="Besonders wichtig ist mir"
                        aria-labelledby={undefined}
                        name="basics"
                      >
                        <FormCheckbox
                          className={styles.checkbox}
                          id="priceGuarantee-checkbox-label"
                          name="priceGuarantee"
                          render={
                            <Checkbox
                              label={
                                <Content
                                  className={styles.checkboxLabel}
                                  size="md"
                                >
                                  Nur mit Preisgarantie
                                </Content>
                              }
                            />
                          }
                        />
                        <FormCheckbox
                          className={styles.checkbox}
                          name="ecoMode"
                          render={
                            <Checkbox
                              label={
                                <Content
                                  className={styles.checkboxLabel}
                                  size="md"
                                >
                                  {productType === 'strom' ||
                                  productType === 'business_strom'
                                    ? `Nur Strom aus erneuerbarer Energie`
                                    : `Nur mit Biogas-Anteil`}
                                </Content>
                              }
                            />
                          }
                        />
                        {isNoBindung && (
                          <FormCheckbox
                            className={styles.checkbox}
                            name="nobindung"
                            render={
                              <Checkbox
                                label={
                                  <Content
                                    className={styles.checkboxLabel}
                                    noSpacing
                                    size="md"
                                  >
                                    Nur ohne Bindung
                                  </Content>
                                }
                              />
                            }
                          />
                        )}
                      </FormGroup>
                    </div>
                  )}
                  {variant !== 'reduced' && (
                    <div className={styles.actions}>
                      <FormSubmit
                        render={
                          <Button
                            className={classNames(styles.actions, {
                              [styles.actionLink]: variant !== 'full',
                            })}
                            isElevated={variant !== 'general'}
                            theme={
                              variant === 'general' ? 'secondary' : 'primary'
                            }
                            variant={variant !== 'general' ? 'link' : null}
                          >
                            {variant === 'general'
                              ? 'Empfohlene Tarife anzeigen'
                              : 'Tarife anzeigen'}
                          </Button>
                        }
                      />
                    </div>
                  )}
                </Form>
              </div>
            </CardBody>
          </Card>
        )}
        {serverError && (
          <Card>
            <CardBody className={styles.errorBody}>
              <ErrorRecommender onNewCheck={handleNewCheck} />
            </CardBody>
          </Card>
        )}
      </div>
      {header && variant === 'full' && (
        <div className={styles.textWrapper}>
          <Heading
            as="h1"
            className={styles.heading}
            size="h1"
            theme="contrast"
          >
            Mit wenigen Klicks zu Ihrem passenden Tarif
          </Heading>
          <Content noSpacing size="md" theme="contrast">
            Unser Tarifempfehler für Strom und Erdgas hilft Ihnen, den für Sie
            besten Tarif zu finden.
          </Content>
        </div>
      )}
    </div>
  );
};

ProductRecommender.propTypes = {
  className: PropTypes.string,
  defaultValues: PropTypes.shape({
    usage: PropTypes.number,
    count: PropTypes.number,
    priceGuarantee: PropTypes.bool,
    ecoMode: PropTypes.bool,
    nobindung: PropTypes.bool,
    zipcode: PropTypes.string,
  }),
  defaultVariableNumber: PropTypes.number,
  header: PropTypes.bool,
  initType: PropTypes.oneOf(['strom', 'gas', 'business_strom', 'business_gas']),
  /** Triggered on a valid form update */
  onChange: PropTypes.func,
  onSubmit: PropTypes.func,
  price: PropTypes.node,
  /** Product ID */
  product: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  variant: PropTypes.oneOf(['full', 'half', 'reduced', 'general']),
};

export default ProductRecommender;
